// Interactive dialogs (shared/API.md §36.4): a plugin or integration answers a slash command or a
// button press by opening a FORM on the reader's screen. Pure half: decoding the socket's
// `open_dialog`, which dialog belongs to THIS client (trigger ids), the form's initial values,
// the webapp's own field checks, and the submit / cancel bodies.
//
// Behaviour follows the Mattermost webapp (components/interactive_dialog + utils/
// integration_utils.checkDialogElementForError) so a plugin written against it behaves the same.
// Twin: windows/src/SatoriChat.Core/Logic/InteractiveDialog.cs (InteractiveDialogTests.cs).

/** 'other' = any type this client has no control for (`date`, `datetime`, a `dynamic` select…): a one-line text field. */
export type DialogElementType = 'text' | 'textarea' | 'select' | 'bool' | 'radio' | 'other';

export interface DialogOption {
  text: string;
  value: string;
}

export interface DialogElement {
  name: string;
  displayName: string;
  type: DialogElementType;
  /** text/textarea: 'text' | 'email' | 'number' | 'password' | 'tel' | 'url' ('' = text). */
  subtype: string;
  /** The default as text ('' = none). A radio / static select default that is not an option is ''. */
  defaultValue: string;
  placeholder: string;
  helpText: string;
  optional: boolean;
  minLength: number;
  /** 0 = the type's own limit (textarea 3000, every other field 150 — the server's). */
  maxLength: number;
  /** select only: 'users' | 'channels' pick a person / channel; '' = the static `options`. */
  dataSource: string;
  options: DialogOption[];
}

export interface Dialog {
  triggerId: string;
  /** Where the submission goes (the server forwards it; the client only echoes it back). */
  url: string;
  callbackId: string;
  title: string;
  introductionText: string;
  iconUrl: string;
  submitLabel: string;
  notifyOnCancel: boolean;
  state: string;
  elements: DialogElement[];
}

/** A field's value while the form is open: text for every type but a bool's checkbox. */
export type DialogValue = string | boolean;
export type DialogValues = Record<string, DialogValue>;

/** Why a field cannot be submitted (§36.4's table; `n` = the length in the sentence). */
export type DialogFieldError =
  | { code: 'required' }
  | { code: 'tooShort'; n: number }
  | { code: 'tooLong'; n: number }
  | { code: 'badEmail' }
  | { code: 'notNumber' }
  | { code: 'badUrl' }
  | { code: 'invalidOption' };

// MARK: - Decoding

function rec(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function s(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function n(v: unknown): number {
  const x = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(x) && x > 0 ? Math.floor(x) : 0;
}

const DRAWN: readonly string[] = ['text', 'textarea', 'select', 'bool', 'radio'];
/** Never drawn, never submitted (§36.4). */
const SKIPPED: readonly string[] = ['file', 'action_button'];

function element(raw: unknown): DialogElement | null {
  const o = rec(raw);
  const name = s(o.name);
  const rawType = s(o.type);
  if (name === '' || SKIPPED.includes(rawType)) return null;
  const dataSource = s(o.data_source);
  let type: DialogElementType = DRAWN.includes(rawType) ? (rawType as DialogElementType) : 'other';
  if (type === 'select' && dataSource !== '' && dataSource !== 'users' && dataSource !== 'channels') type = 'other';
  const options = (Array.isArray(o.options) ? o.options : [])
    .map((x) => ({ text: s(rec(x).text), value: s(rec(x).value) }))
    .filter((x) => x.text !== '' || x.value !== '');
  let defaultValue = s(o.default);
  const fixedChoices = type === 'radio' || (type === 'select' && dataSource === '');
  if (fixedChoices && !options.some((x) => x.value === defaultValue)) defaultValue = '';
  return {
    name,
    displayName: s(o.display_name) || name,
    type,
    subtype: s(o.subtype),
    defaultValue,
    placeholder: s(o.placeholder),
    helpText: s(o.help_text),
    optional: o.optional === true,
    minLength: n(o.min_length),
    maxLength: n(o.max_length),
    dataSource: type === 'select' ? dataSource : '',
    options,
  };
}

/** The dialog object (`dialog` of the event, or a submit answer's `form`) onto a request's url + trigger. */
function dialogFrom(d: Record<string, unknown>, url: string, triggerId: string): Dialog | null {
  const title = s(d.title);
  if (url === '' || title === '') return null;
  return {
    triggerId,
    url,
    callbackId: s(d.callback_id),
    title,
    introductionText: s(d.introduction_text),
    iconUrl: s(d.icon_url),
    submitLabel: s(d.submit_label),
    notifyOnCancel: d.notify_on_cancel === true,
    state: s(d.state),
    elements: (Array.isArray(d.elements) ? d.elements : []).map(element).filter((x): x is DialogElement => x !== null),
  };
}

/**
 * The `open_dialog` event's `data` → a dialog, or null. `data.dialog` is the OpenDialogRequest as a
 * JSON STRING (an object is accepted too). A dialog with no url or no title is unusable → null.
 */
export function decodeOpenDialog(data: unknown): Dialog | null {
  let raw: unknown = rec(data).dialog;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const req = rec(raw);
  return dialogFrom(rec(req.dialog), s(req.url), s(req.trigger_id));
}

// MARK: - Which dialog is ours (trigger ids)

/** Where a trigger id was minted: the dialog submits against this conversation and team. */
export interface DialogContext {
  channelId: string;
  teamId: string;
}

/** How long a remembered trigger id stays good (§36.4: 3 minutes). */
export const TRIGGER_TTL_MS = 3 * 60_000;
/** How long an unmatched dialog waits for its trigger id (§36.4: 10 s). */
export const HOLD_MS = 10_000;

/**
 * The server sends `open_dialog` to EVERY session of the user; only the client whose command or
 * button press minted the trigger id opens it (webapp `handleOpenDialogEvent`) — otherwise a form
 * would pop up on the reader's other computer and phone too. The event can beat the HTTP answer
 * that names the trigger (the plugin opens the dialog inside the command handler): an unmatched
 * dialog is HELD for `HOLD_MS` and opens if its trigger id lands in that window.
 */
export class DialogTriggers {
  private readonly triggers = new Map<string, DialogContext & { at: number }>();
  private readonly held: { dialog: Dialog; at: number }[] = [];

  private prune(now: number) {
    for (const [id, v] of this.triggers) if (now - v.at > TRIGGER_TTL_MS) this.triggers.delete(id);
    for (let i = this.held.length - 1; i >= 0; i--) if (now - this.held[i].at > HOLD_MS) this.held.splice(i, 1);
  }

  /** A command / action answer named `triggerId`: a held dialog to open now (with its context), if any. */
  noteTrigger(triggerId: string, context: DialogContext, now: number): { dialog: Dialog; context: DialogContext } | null {
    this.prune(now);
    if (triggerId === '') return null;
    const i = this.held.findIndex((h) => h.dialog.triggerId === triggerId);
    if (i >= 0) return { dialog: this.held.splice(i, 1)[0].dialog, context };
    this.triggers.set(triggerId, { ...context, at: now });
    return null;
  }

  /** The socket delivered a dialog: its context = open it now; null = held (or never ours). */
  offer(dialog: Dialog, now: number): DialogContext | null {
    this.prune(now);
    const known = dialog.triggerId === '' ? undefined : this.triggers.get(dialog.triggerId);
    if (known) {
      this.triggers.delete(dialog.triggerId);
      return { channelId: known.channelId, teamId: known.teamId };
    }
    this.held.push({ dialog, at: now });
    return null;
  }
}

// MARK: - The form

/** Each field's starting value: its default; a checkbox is on for "true", "1" or "yes". */
export function initialValues(elements: readonly DialogElement[]): DialogValues {
  const out: DialogValues = {};
  for (const e of elements) out[e.name] = e.type === 'bool' ? ['true', '1', 'yes'].includes(e.defaultValue.trim().toLowerCase()) : e.defaultValue;
  return out;
}

/** The longest value a field takes: `max_length`, else the server's 3000 for a textarea and 150 otherwise. */
export function maxLength(e: DialogElement): number {
  if (e.maxLength > 0) return e.maxLength;
  return e.type === 'textarea' ? 3000 : 150;
}

const NUMBER = /^[-+]?(\d+\.?\d*|\.\d+)$/;

/** UTF-16 code units, like the webapp's `.length` (§36.4). */
const len = (v: string) => v.length;

/** One field's check (§36.4's table, in order; first failure wins), null = fine. */
export function fieldError(e: DialogElement, value: DialogValue | undefined): DialogFieldError | null {
  const empty = value === undefined || value === false || value === '';
  if (empty && !e.optional) return { code: 'required' };
  const v = typeof value === 'string' ? value : '';
  if (e.type === 'text' || e.type === 'textarea') {
    if (v !== '' && len(v) < e.minLength) return { code: 'tooShort', n: e.minLength };
    if (len(v) > maxLength(e)) return { code: 'tooLong', n: maxLength(e) };
    if (v !== '' && e.subtype === 'email' && !v.includes('@')) return { code: 'badEmail' };
    if (v !== '' && e.subtype === 'number' && !NUMBER.test(v.trim())) return { code: 'notNumber' };
    if (v !== '' && e.subtype === 'url' && !v.includes('http://') && !v.includes('https://')) return { code: 'badUrl' };
  }
  if ((e.type === 'radio' || (e.type === 'select' && e.dataSource === '')) && v !== '' && !e.options.some((o) => o.value === v)) return { code: 'invalidOption' };
  return null;
}

/** Every field's check; an empty map = submittable. */
export function validate(elements: readonly DialogElement[], values: DialogValues): Record<string, DialogFieldError> {
  const out: Record<string, DialogFieldError> = {};
  for (const e of elements) {
    const err = fieldError(e, values[e.name]);
    if (err) out[e.name] = err;
  }
  return out;
}

/**
 * The `submission` map (§36.4): every element whose value is not empty — a checkbox always, as a
 * boolean; a `number` field as a JSON number; everything else the text as typed.
 */
export function submission(elements: readonly DialogElement[], values: DialogValues): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const e of elements) {
    const v = values[e.name];
    if (e.type === 'bool') {
      out[e.name] = v === true;
      continue;
    }
    const text = typeof v === 'string' ? v : '';
    if (text === '') continue;
    if (e.subtype === 'number' && (e.type === 'text' || e.type === 'textarea') && NUMBER.test(text.trim())) {
      out[e.name] = Number(text.trim());
      continue;
    }
    out[e.name] = text;
  }
  return out;
}

/** `POST /api/v4/actions/dialogs/submit` body for a submission, in the trigger's context. */
export function submitBody(dialog: Dialog, values: DialogValues, context: DialogContext): Record<string, unknown> {
  return {
    url: dialog.url,
    callback_id: dialog.callbackId,
    state: dialog.state,
    channel_id: context.channelId,
    team_id: context.teamId,
    submission: submission(dialog.elements, values),
    cancelled: false,
  };
}

/** The same endpoint's body for a cancel — sent only when the dialog asked (`notify_on_cancel`). */
export function cancelBody(dialog: Dialog, context: DialogContext): Record<string, unknown> | null {
  if (!dialog.notifyOnCancel) return null;
  return {
    url: dialog.url,
    callback_id: dialog.callbackId,
    state: dialog.state,
    channel_id: context.channelId,
    team_id: context.teamId,
    submission: {},
    cancelled: true,
  };
}

/** What the submit answered (§36.4). */
export type SubmitOutcome =
  /** Accepted (`{}`, `type: "ok"` / `"navigate"`): close the form. */
  | { kind: 'done' }
  /** The integration refused: keep the form; `fieldErrors` under their fields, `error` above them. */
  | { kind: 'errors'; error: string; fieldErrors: Record<string, string> }
  /** `type: "form"`: the dialog is replaced in place by the next step. */
  | { kind: 'form'; dialog: Dialog }
  /** The request itself failed (network / non-2xx): keep the form, say so above the fields. */
  | { kind: 'failed' };

/**
 * The 2xx answer `{error?, errors?, type?, form?}` → outcome. Field errors count only for fields
 * the form HAS (webapp checkIfErrorsMatchElements); keys that match no element are ignored.
 */
export function submitOutcome(body: unknown, dialog: Dialog): SubmitOutcome {
  const o = rec(body);
  const error = s(o.error).trim();
  const names = new Set(dialog.elements.map((e) => e.name));
  const fieldErrors: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec(o.errors))) {
    const text = s(v).trim();
    if (names.has(k) && text !== '') fieldErrors[k] = text;
  }
  if (error !== '' || Object.keys(fieldErrors).length > 0) return { kind: 'errors', error, fieldErrors };
  if (s(o.type) === 'form') {
    const next = dialogFrom({ title: dialog.title, ...rec(o.form) }, dialog.url, dialog.triggerId);
    if (next) return { kind: 'form', dialog: next };
  }
  return { kind: 'done' };
}

/** The submit button's label: the dialog's own, else the localised «Enviar». */
export function submitLabel(dialog: Dialog, fallback: string): string {
  return dialog.submitLabel.trim() || fallback;
}
